import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { API_URLS } from "../../config";
import { apiRequest } from "../../utils/apiRequest";
import AccessDenied from "../../Components/AccessDenied";
import {
  FiEdit, FiPlus, FiEye, FiTrash2, FiChevronUp, FiChevronDown, FiAward, FiRefreshCw, FiCheck, FiX, FiUploadCloud
} from "react-icons/fi";
import MasterUploadModal from "../../Components/MasterUploadModal";
import { useToast } from '../../Components/Toast';
import FilterPanel, { FilterField } from "../../Components/FilterPanel";

/* ── helpers ─────────────────────────────────────────────────── */
const EMPTY_FORM = {
  name: "",
};

const Certificates = () => {
  const { token, user } = useAuth();
  const toast = useToast();

  /* list state */
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  /* selection state */
  const [selectedIds, setSelectedIds] = useState([]);
  const [isTableCollapsed, setIsTableCollapsed] = useState(false);

  /* filter state */
  const [search, setSearch] = useState("");


  /* modal state */
  const [modal, setModal] = useState(null); // "add" | "edit" | "view"
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  /* ── fetch list ─────────────────────────────────────────────── */
  const fetchRows = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit });
    if (search) params.set("search", search);


    const result = await apiRequest(`${API_URLS.CERTIFICATES}?${params}`, {}, token);
    if (!result) return;
    if (result.denied) { setDenied(result); setLoading(false); return; }
    if (result.ok) {
      setRows(result.data.rows || []);
      setTotal(result.data.total || 0);
      setSelectedIds([]);
    }
    setLoading(false);
  }, [token, page, search, limit]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  /* ── Selection ── */
  const toggleSelectRow = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === rows.length && rows.length > 0) setSelectedIds([]);
    else setSelectedIds(rows.map(r => r.id));
  };

  /* ── modal helpers ──────────────────────────────────────────── */
  const initialFormSnapshotRef = useRef(null);
  const openAdd = () => { setFormData(EMPTY_FORM); initialFormSnapshotRef.current = JSON.stringify(EMPTY_FORM); setModal("add"); };
  const openEdit = () => {
    if (selectedIds.length !== 1) return;
    const item = rows.find(r => r.id === selectedIds[0]);
    if (item) {
      setFormData({ ...item });
      initialFormSnapshotRef.current = JSON.stringify({ ...item });
      setModal("edit");
    }
  };
  const openView = () => {
    if (selectedIds.length !== 1) return;
    const item = rows.find(r => r.id === selectedIds[0]);
    if (item) {
      setFormData(item);
      setModal("view");
    }
  };
  const closeModal = () => { setModal(null); setFormData(EMPTY_FORM); };

  // Unsaved-changes guard — only relevant for add/edit, never for read-only view
  const isFormDirty = () => modal !== "view" && initialFormSnapshotRef.current !== null && JSON.stringify(formData) !== initialFormSnapshotRef.current;
  const requestCloseModal = () => {
    if (isFormDirty()) {
      toast.confirm(
        "You have unsaved changes. Do you want to save before closing?",
        () => handleSave(),
        () => closeModal()
      );
    } else {
      closeModal();
    }
  };

  const handleFormChange = e => setFormData(f => ({ ...f, [e.target.name]: e.target.value }));

  /* ── save ───────────────────────────────────────────────────── */
  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    const isEdit = modal === "edit";
    const url = isEdit ? `${API_URLS.CERTIFICATES}/${selectedIds[0]}` : API_URLS.CERTIFICATES;
    const method = isEdit ? "PUT" : "POST";
    const result = await apiRequest(url, { method, body: formData }, token);
    setSaving(false);
    if (result?.ok) {
      toast.success("Certificate saved successfully!");
      closeModal();
      fetchRows();
    } else toast.error(result?.data?.message || "Failed");
  };

  /* ── delete ─────────────────────────────────────────────────── */
  const handleDelete = () => {
    if (selectedIds.length === 0) return;
    const msg = selectedIds.length === 1
      ? "Delete this certificate?"
      : `Delete ${selectedIds.length} selected certificates?`;

    toast.deleteConfirm(msg, async () => {
      setLoading(true);
      let successCount = 0;
      for (const id of selectedIds) {
        const res = await apiRequest(`${API_URLS.CERTIFICATES}/${id}`, { method: "DELETE" }, token);
        if (res?.ok) successCount++;
      }
      setLoading(false);
      if (successCount > 0) {
        toast.success(`Deleted ${successCount} items.`);
        fetchRows();
      } else toast.error("Failed to delete items.");
    });
  };

  /* ── export ─────────────────────────────────────────────────── */
  const handleExport = async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);


    const res = await fetch(`${API_URLS.CERTIFICATES}/export?${params}`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Certificates_${Date.now()}.xlsx`;
      a.click();
    }
  };

  const pages = Math.ceil(total / limit);

  if (denied) return (
    <div className="vendor-page">
      <div className="cert-management-card">
        <AccessDenied message={denied.message} required={denied.required} />
      </div>
    </div>
  );

  return (
    <div className="vendor-page">

      {/* ── Action Bar ── */}
      <div className="vendor-action-bar">
        <button className="new-user-btn" onClick={openAdd}><FiPlus /> New Certificate</button>
        <div style={{ width: 1, height: 24, background: "#ccc", margin: "0 8px" }} />
        <button className="action-btn view-btn" disabled={selectedIds.length !== 1} onClick={openView}><FiEye /> View</button>
        <button className="action-btn edit-btn" disabled={selectedIds.length !== 1} onClick={openEdit}><FiEdit /> Edit</button>
        <button className="action-btn delete-btn" disabled={selectedIds.length === 0} onClick={handleDelete}><FiTrash2 /> Delete</button>
        {['global_superadmin', 'company_admin'].includes(user?.role) && (
          <>
            <button className="action-btn export-btn" onClick={handleExport}><FiRefreshCw size={13} /> Export</button>
            <button className="action-btn upload-btn" onClick={() => setIsUploadOpen(true)}><FiUploadCloud /> Upload</button>
          </>
        )}
        <FilterPanel
          title="Filter certificates"
          width={320}
          columns={1}
          values={{ search: search }}
          emptyValues={{ search: "" }}
          onChange={(v) => { setSearch(v.search); setPage(1); }}
        >
          {(filters, set) => (
            <>
              <FilterField label="Search" wide>
                <input autoFocus placeholder="Search certificate..." value={filters.search}
                  onChange={(e) => set({ search: e.target.value })} />
              </FilterField>
            </>
          )}
        </FilterPanel>
      </div>

      {/* ── Table Card ── */}
      <div className="management-card">
        <div className="management-header" onClick={() => setIsTableCollapsed(!isTableCollapsed)}>
          <div className="header-title">
            <FiAward size={18} />
            <h2>Certificate Master</h2>
            <span className="vendor-badge">{total}</span>
          </div>
          <div className="header-actions">
            {isTableCollapsed ? <FiChevronDown /> : <FiChevronUp />}
          </div>
        </div>

        {!isTableCollapsed && (
          <div className="vendor-table-container">
            {loading && rows.length === 0 ? <div style={{ padding: 20 }}>Loading...</div> : (
              <table className="vendor-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        checked={rows.length > 0 && selectedIds.length === rows.length}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th style={{ width: 80 }}>SR. NO.</th>
                    <th>CERTIFICATE NAME</th>

                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={3} style={{ textAlign: "center", padding: 40 }}>No certificates found</td></tr>
                  ) : rows.map((r, i) => (
                    <tr
                      key={r.id || i}
                      className={selectedIds.includes(r.id) ? "selected" : ""}
                      onClick={() => toggleSelectRow(r.id)}
                    >
                      <td onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(r.id)}
                          onChange={() => toggleSelectRow(r.id)}
                        />
                      </td>
                      <td>{r.srNo}</td>
                      <td>{r.name}</td>

                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Pagination */}
            {total > 0 && (
              <div className="cust-pagination">
                <select
                  value={limit}
                  onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                  style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}
                >
                  {[25, 50, 75, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="cust-page-info">Page {page} of {Math.max(1, pages)}</span>
                <button className="cust-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                <button className="cust-btn" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next</button>
              </div>
            )}
          </div>
        )}
      </div>

      {modal && (
        <div className="vendor-modal-overlay" onClick={requestCloseModal}>
          <div className="vendor-modal" onClick={e => e.stopPropagation()} style={{ width: "85%", maxWidth: "600px" }}>
            <div style={{ background: "#1a2a4d", color: "#fff", padding: "12px 30px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 600, fontSize: 16 }}>{modal.toUpperCase()} CERTIFICATE</span>
              <button onClick={requestCloseModal} style={{ background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center" }}><FiX /></button>
            </div>
            <div style={{ padding: 30 }}>
              <form onSubmit={handleSave}>
                <div className="form-grid" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>
                  <div className="form-field">
                    <label>Certificate Name <span className="required">*</span></label>
                    <input name="name" value={formData.name} onChange={handleFormChange} required disabled={modal === 'view'} placeholder="Enter certificate name" />
                  </div>

                </div>

                <div className="form-footer" style={{ marginTop: 30, textAlign: "right", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                  {modal !== 'view' && (
                    <button type="submit" className="save-btn" disabled={saving}><FiCheck /> Save</button>
                  )}
                  <button type="button" className="cancel-btn" onClick={requestCloseModal}>{modal === 'view' ? "Close" : "Cancel"}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      <MasterUploadModal
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        title="Upload Certificates"
        templateUrl={`${API_URLS.CERTIFICATES}/template`}
        uploadUrl={`${API_URLS.CERTIFICATES}/upload`}
        instructions={[
          "Certificate Name is a mandatory field.",

          "Ensure the certificate name is unique."
        ]}
        onSuccess={fetchRows}
        token={token}
      />
    </div>
  );
};

export default Certificates;
